// Steps the homepage product preview through its sidebar pages on a timer, like a short tour.
// It only runs while the preview is on screen in a visible browser tab (browsers freeze CSS
// animations in background tabs but not timers), and gives a manually selected page a fresh
// interval before continuing. `data-tour` on the preview mirrors that state so the progress ring
// on the current page can follow along in CSS. Every page stays rendered, so the flow map's SMIL
// pulses are paused whenever they can't be seen.
const STEP_MS = 4500;

const PreviewTourHooks = {
  MarketingPreviewTour: {
    mounted() {
      this.pulses = this.el.querySelector('[data-preview="flows"] svg');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.pulses?.pauseAnimations();
        return;
      }

      this.enabled = true;
      this.visible = false;
      this.tab = this.currentTab();
      this.remaining = STEP_MS;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.visible = entry.isIntersecting;
          this.apply();
        },
        { threshold: 0.4 },
      );
      this.observer.observe(this.el);

      this.restart = event => {
        const selectedTab = event.target.closest('[phx-value-tab]');
        if (!event.isTrusted || !selectedTab) return;

        this.halt();
        this.remaining = STEP_MS;
        this.apply();
      };

      this.el.addEventListener('click', this.restart);

      this.onVisibilityChange = () => this.apply();
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.apply();
    },

    updated() {
      if (!this.enabled) return;

      // A new page gets its full time on screen.
      const tab = this.currentTab();
      if (tab !== this.tab) {
        this.tab = tab;
        this.halt();
        this.remaining = STEP_MS;
      }

      // LiveView's patch drops attributes it didn't render, so restore ours.
      this.apply();
    },

    destroyed() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },

    state() {
      return this.visible && !document.hidden ? 'running' : 'paused';
    },

    apply() {
      const state = this.state();
      this.el.dataset.tour = state;

      const pulsesSeen = this.visible && !document.hidden && this.currentTab() === 'flows';
      if (pulsesSeen) this.pulses?.unpauseAnimations();
      else this.pulses?.pauseAnimations();

      if (state === 'running' && !this.timer) {
        this.deadline = Date.now() + this.remaining;
        this.timer = setTimeout(() => this.advance(), this.remaining);
      } else if (state !== 'running') {
        this.halt();
      }
    },

    halt() {
      if (!this.timer) return;

      clearTimeout(this.timer);
      this.timer = null;
      this.remaining = Math.max(this.deadline - Date.now(), 0);
    },

    currentTab() {
      return this.el.querySelector('[phx-value-tab][aria-pressed="true"]')?.getAttribute('phx-value-tab');
    },

    advance() {
      this.timer = null;

      const tabs = [...this.el.querySelectorAll('[phx-value-tab]')];
      const current = tabs.findIndex(tab => tab.getAttribute('aria-pressed') === 'true');
      const next = tabs[(current + 1) % tabs.length];
      this.pushEvent('preview_tab', { tab: next.getAttribute('phx-value-tab') });
    },
  },
};

export default PreviewTourHooks;
